import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import app from 'flarum/forum/app';
import Api from '../utils/Api';
import { formatAmount, parseAmountInput, formatDelta, tierClassName, tierOrdinalText } from '../utils/refundFormat';
// v0.19.20 (批 41 · 段②) — 「后端错误文案」改用**共用入口**
//   (本仓铁律「一个逻辑一个入口, 不许抄两遍」; 改前这个原语在本文件里抄了一份)。
import { errText } from '../utils/FormPrimitives';
// v0.19.22 (批 43) — 「审批档序号」改用**共用入口** (改前本文件与 RefundApproveModal 各抄了一份)
import { tierOrder } from '../utils/RefundTier';
import RefundResultModal from './RefundResultModal';

/**
 * v0.18.62 (辉哥 10/23 拍板) — **发起退卡 / 退费申请** modal (设计稿 v4 · S2)
 *
 * 视觉基线: `public/assets/ziven-scheduler-refund-mockup.html` 的 `?scene=request`
 *   (桌面 + 移动 + over/under/big 三态)。**类名 / 配色 / 间距全部照抄设计稿。**
 *
 * ## ⛔ 三条不可违背的红线 (设计稿明文)
 *   1. **前端不重算退费公式** ⇒ 「系统核算额」**只能**来自 `GET …/refund/preview`。
 *   2. **前端不判审批档** ⇒ 档位文案 (`approval_label`) / 档位序号 (`tier_order`) /
 *      整句提示 (`tier_hint`) **只能**来自同一个预览端点;
 *      ⛔ **不建** `tier → 文案` / `tier → tier-N` 映射表。
 *   3. **可见性只读后端下发的能力标志** ⇒ `can_refund`。
 *
 * ## 金额完全自由输入 (辉哥 10/22 拍板)
 *   · ⛔ **不拦**低于系统核算额 · ⛔ **不设**上限 · ⛔ **不做**整数约束
 *   · ⛔ 金额输入框**任何时候都不 disabled** (四态都不拦)
 *   · 差额 ≠ 0 ⇒ **提示** + 说明**必填** (⛔ 拦的是「没写原因」, 不是「金额大小」)
 *
 * ## 提交
 *   `POST …/student-cards/{id}/refund` · `{ refund_reason, refund_amount, refund_note }`
 *   · `refund_amount` 发**纯数字** (⛔ 不发带千分位的字符串)
 *   · 成功 ⇒ 关本弹窗 ⇒ 弹 `RefundResultModal`
 *
 * ## v0.18.75 (D4) — 三个原因各自带一枚「预计退款额」角标
 *   · 角标金额**只能**来自 `GET …/refund/preview?reason=…` 的 `system_amount`
 *     ⇒ ⛔ 前端**不重算退费公式**, 也**不判**三档会不会分叉。
 *   · 三个原因一次性拉齐 (`loadReasonPreviews()`); 切原因**只**改 `.is-on` 态,
 *     ⛔ 角标数值不随切换重算。
 *   · 加载中留空 / 预览失败显示 `≈—` (⛔ 不许显示 ¥0)。
 */

/**
 * 三个退费原因 (**逐字照抄设计稿**: 标题 + 依据说明)。
 *
 * ⚠️ 这三行是**设计稿文案**, 不是「档位 → 文案」那种会漂移的映射 ——
 *   退费原因是**写死的三值枚举** (后端 `RefundCalculator::REASONS`), 文案不会变。
 */
const REASON_OPTIONS = [{
  key: 'institution',
  title: '机构原因',
  desc: '机构侧原因终止服务，按整卡实收与课时比例退回剩余部分。'
}, {
  key: 'consumer',
  title: '消费者原因',
  desc: '消费者侧提出，按打折前原价扣减已消费部分后退回余额。'
}, {
  key: 'mutual',
  title: '协商一致',
  desc: '双方协商一致，按剩余课时对应金额一次性退回。'
}];

/** 说明框字数上限 (与后端 `RequestRefundController` 的 500 字校验一致) */
const NOTE_MAX = 500;
export default class RefundRequestModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.card = vnode.attrs.card || {};

    // 原因默认值 = **`consumer` 消费者原因** (设计稿 v4 `?scene=request` 的默认态:
    //   L1031-1037 —— `is-on` 挂在**第 2 条**「消费者原因」, 第 1 条「机构原因」**没有** is-on)。
    //   ⚠️ 显示**顺序**仍是 机构 → 消费者 → 协商 (REASON_OPTIONS 的顺序照设计稿, 没动);
    //      改的只是**默认选中项**。
    //   ⚠️ 金额框预填 / 「系统核算」行的数 = **后端**按当前原因算出来的 (⛔ 前端不写死金额)。
    this.reason = 'consumer';
    // 金额输入框的**原始字符串** (⛔ 不在输入过程中改写它 —— 用户正在打字时不能被格式化打断)
    this.amount = '';
    this.note = '';

    // 预览端点的响应 (⛔ 系统核算额 / 审批档 **只能**来自这里)
    this.preview = null;
    this.previewLoading = true;

    // v0.18.75 (D4) — **三个原因各自的** preview (一次性拉齐 ⇒ 角标不用切原因就重算)
    //   ⛔ 里面每个金额都是后端 `system_amount`, 前端一个数都不自己算。
    this.reasonPreviews = {};
    // v0.18.75 (D4) — 三个角标是否还在加载 (加载中 ⇒ 角标留空, ⛔ 不许显示旧数)
    this.preasonLoading = true;
    this.error = '';
    this.saving = false;
    this.loadPreview(this.reason);
    this.loadReasonPreviews();
  }
  className() {
    return 'Modal--small Modal--fullscreen-padded ZS-RefundRequestModal';
  }
  title() {
    return m("span", null, m("i", {
      className: "fas fa-coins",
      style: "color:var(--zs-danger-ef4444, #ef4444);margin-right:8px;"
    }), "\u7533\u8BF7\u9000\u5361");
  }

  // ─────────────────────────────────────────────────────────────────────
  // 预览 (只读)
  // ─────────────────────────────────────────────────────────────────────

  /**
   * 拉一次预览 (系统核算额 + 审批档)。
   *
   * ⚠️ **切原因 ⇒ 必须重拉**: 三套公式的核算额不同 ⇒ 金额输入框也要**重置**。
   */
  async loadPreview(reason) {
    this.previewLoading = true;
    this.error = '';
    m.redraw();
    try {
      const r = await Api.refundPreview(this.card.id, reason);
      this.preview = r || null;
      // 金额输入框**重置**为新的系统核算额 (brief §5.3)
      this.amount = this.preview && this.preview.system_amount !== null && this.preview.system_amount !== undefined ? formatAmount(this.preview.system_amount) : '';
    } catch (e) {
      this.preview = null;
      this.error = errText(e, '退费预览失败');
    } finally {
      this.previewLoading = false;
      m.redraw();
    }
  }

  /**
   * v0.18.75 (D4) — **一次把三个原因的 preview 都拉回来** (角标用)
   *
   * ## 为什么单独拉一遍 (而不是切原因时才拉)
   *   角标的作用是「**选原因之前**就看到这个原因大概退多少钱」 ⇒ 三个都得同时在。
   *   切原因只改 `.is-on` 态, ⛔ **不重算角标** (切一次重算一次 = 三个角标数字乱跳)。
   *
   * ## ⛔ 红线
   *   每个角标的金额**只能**是后端 `system_amount` ⇒ 前端**不重算退费公式**,
   *   也**不判**「三档会不会分叉」(分叉取决于卡种原价 / 赠课, 只有后端知道)。
   */
  async loadReasonPreviews() {
    this.preasonLoading = true;
    m.redraw();
    try {
      const reasons = REASON_OPTIONS.map(o => o.key); // ['institution','consumer','mutual']
      const results = await Promise.all(reasons.map(r => Api.refundPreview(this.card.id, r).catch(() => null)));
      const map = {};
      reasons.forEach((r, i) => {
        map[r] = results[i];
      });
      this.reasonPreviews = map;
    } finally {
      this.preasonLoading = false;
      m.redraw();
    }
  }

  /**
   * v0.18.75 (D4) — 角标金额: **只读后端**, 一个数都不自己算
   *
   * · 加载中 ⇒ '' (留空; ⛔ 不许显示旧原因的数, 切原因时会误导)
   * · 预览失败 / 金额为 null ⇒ '≈—' (⛔ **不许**显示 ¥0 —— 0 会被误读成「退 0 元」)
   */
  reasonAmount(key) {
    if (this.preasonLoading) return ''; // 加载中 ⇒ 留空 (不显示旧数)
    const p = this.reasonPreviews[key];
    if (!p || p.system_amount === null || p.system_amount === undefined) return '≈—';
    return '≈¥' + formatAmount(p.system_amount);
  }

  /** 后端错误的 `detail` 文案 (⛔ 不自己编文案)
   *  ⚠️ v0.19.20 (批 41 · 段②) — 实现**搬**进共用入口 `utils/FormPrimitives.js`
   *    (改前 `errText` 在本文件里抄了一份 ⇒ 本仓铁律「一个逻辑一个入口」)。 */

  // ─────────────────────────────────────────────────────────────────────
  // 差额 (手填额 vs 系统核算额)
  // ─────────────────────────────────────────────────────────────────────

  /**
   * 当前的差额情况。
   *
   * @returns {{sys:number, filled:number, diff:number, over:boolean, under:boolean}|null}
   *   null = 没有预览 / 没填 / 填的不是数字 ⇒ **不显示**差额相关 UI
   */
  deltaInfo() {
    const p = this.preview;
    if (!p || p.system_amount === null || p.system_amount === undefined) return null;
    const sys = Number(p.system_amount);
    if (!isFinite(sys)) return null;
    const filled = parseAmountInput(this.amount);
    if (filled === null || !isFinite(filled)) return null;

    // 半分的容差: 系统核算额是 2 位小数, 手填额可能多打几位
    const diff = filled - sys;
    if (Math.abs(diff) < 0.005) {
      return {
        sys,
        filled,
        diff: 0,
        over: false,
        under: false
      };
    }
    return {
      sys,
      filled,
      diff,
      over: diff > 0,
      under: diff < 0
    };
  }

  /** 差额 ≠ 0 ⇒ 说明必填 (brief §5.3) */
  noteRequired() {
    const d = this.deltaInfo();
    return !!(d && d.diff !== 0);
  }

  // ─────────────────────────────────────────────────────────────────────
  // 渲染
  // ─────────────────────────────────────────────────────────────────────

  content() {
    const card = this.card || {};
    const studentName = card.student_display_name || '学员';
    const preview = this.preview;
    const delta = this.deltaInfo();
    const noteLen = this.note.length;
    const overLimit = noteLen > NOTE_MAX;

    // ⚠️ Flarum 2.0 的 Modal **不渲染** `.Modal-body` 外壳 (content() 直接进 .Modal-content)
    //   ⇒ 盒子模型由 `.ZS-RefundModal-body` 自己扛 (见 less/forum.less 的 v0.18.62 段),
    //   footer 与 body 是**兄弟** (设计稿 DOM 逐字同款)。
    return m('[', null, m("div", {
      className: "ZS-RefundModal-body"
    }, m("div", {
      className: "ZS-RefundModal-cardinfo"
    }, m("div", {
      className: "ZS-RefundModal-cardname"
    }, m("i", {
      className: "fas fa-id-card",
      style: "color:var(--zs-cyan-06b6d4, #06b6d4);margin-right:8px;"
    }), card.template_name || `学员卡 #${card.id}`), m("div", {
      className: "ZS-RefundModal-stats"
    }, m("span", null, "\u5B66\u5458: ", m("strong", null, studentName)), m("span", null, "\u603B\u8BFE\u65F6: ", m("strong", null, card.total_lessons)), m("span", null, "\u5269\u4F59: ", m("strong", null, card.remaining_lessons)), m("span", null, "\u552E\u4EF7: ", m("strong", {
      className: "money"
    }, "\xA5", formatAmount(card.price))))), m("div", {
      className: "Form-group"
    }, m("label", null, m("i", {
      className: "fas fa-scale-balanced",
      style: "color:var(--zs-purple-8b5cf6, #8b5cf6)"
    }), " \u9000\u8D39\u539F\u56E0"), m("div", {
      className: "ZS-RefundModal-reasons"
    }, REASON_OPTIONS.map(opt => m("div", {
      key: opt.key,
      className: 'ZS-RefundModal-reason' + (this.reason === opt.key ? ' is-on' : ''),
      onclick: () => this.pickReason(opt.key)
    }, m("span", {
      className: "radio-dot"
    }), m("div", {
      className: "rr-main"
    }, m("div", {
      className: "rr-head"
    }, m("div", {
      className: "rr-title"
    }, opt.title), m("span", {
      className: "rr-amt"
    }, m("i", {
      className: "fas fa-calculator"
    }), this.reasonAmount(opt.key))), m("div", {
      className: "rr-desc"
    }, opt.desc))))), m("div", {
      className: "ZS-RefundModal-amtnote"
    }, m("i", {
      className: "fas fa-circle-info"
    }), m("div", null, "\u89D2\u6807 = \u6309\u5F53\u524D\u8FD9\u5F20\u5361\u7B97\u51FA\u7684", m("b", null, "\u9884\u8BA1"), "\u9000\u6B3E\u989D\uFF0C\u6700\u7EC8\u4EE5\u7CFB\u7EDF\u6838\u7B97\u989D\u4E3A\u51C6\u3002")), m("div", {
      className: "helpText"
    }, "\u4E09\u79CD\u539F\u56E0\u5BF9\u5E94\u4E0D\u540C\u7684\u9000\u6B3E\u4F9D\u636E\uFF1B\u9000\u6B3E\u91D1\u989D\u53EF\u81EA\u7531\u586B\u5199\uFF0C\u7CFB\u7EDF\u6838\u7B97\u91D1\u989D\u4EC5\u4F5C\u53C2\u7167\u3002")), m("div", {
      className: "Form-group ZS-RefundModal-note"
    }, m("label", null, m("i", {
      className: "fas fa-file-lines",
      style: "color:var(--zs-brand-3b82f6, #3b82f6)"
    }), " \u8BF4\u660E", this.noteRequired() && m("span", {
      className: "ZS-RefundModal-req",
      style: "display:inline-flex"
    }, "\u5FC5\u586B")), m("textarea", {
      className: 'FormControl' + (overLimit ? ' is-error' : ''),
      rows: "2",
      placeholder: "\u8865\u5145\u672C\u6B21\u9000\u5361\u7684\u80CC\u666F\uFF0C\u4F8B\u5982\u8BFE\u65F6\u534F\u5546\u8FC7\u7A0B\u3001\u5BB6\u957F\u53CD\u9988\u7B49",
      value: this.note,
      oninput: e => {
        this.note = e.target.value;
        m.redraw();
      }
    }), m("div", {
      className: 'ZS-RefundModal-counter' + (overLimit ? ' over' : '')
    }, noteLen, "/", NOTE_MAX), this.noteRequired() && this.note.trim() === '' && m("div", {
      className: "ZS-RefundModal-required"
    }, m("i", {
      className: "fas fa-circle-exclamation"
    }), m("span", null, "\u9000\u6B3E\u91D1\u989D\u4E0E\u7CFB\u7EDF\u6838\u7B97\u91D1\u989D\u4E0D\u4E00\u81F4\uFF0C\u8BF7\u5728\u8BF4\u660E\u4E2D\u5199\u660E\u539F\u56E0\u3002"))), m("div", {
      className: "ZS-RefundModal-cols"
    }, m("div", {
      className: "Form-group"
    }, m("label", null, m("i", {
      className: "fas fa-yen-sign",
      style: "color:var(--zs-warning-f59e0b, #f59e0b)"
    }), " \u9000\u6B3E\u91D1\u989D"), m("div", {
      className: "ZS-RefundModal-amountInput"
    }, m("span", {
      className: "cur"
    }, "\xA5"), m("input", {
      type: "text",
      inputmode: "decimal",
      "aria-label": "\u9000\u6B3E\u91D1\u989D",
      value: this.amount,
      oninput: e => {
        this.amount = e.target.value;
        m.redraw();
      }
    })), preview && m("div", {
      className: "ZS-RefundModal-ref"
    }, m("i", {
      className: "fas fa-circle-info"
    }), "\u7CFB\u7EDF\u6838\u7B97 ", m("b", null, "\xA5", formatAmount(preview.system_amount)), "\uFF08\u4EC5\u4F5C\u53C2\u7167\uFF09", delta && delta.diff !== 0 && m("span", {
      className: "ZS-RefundModal-delta"
    }, delta.over ? '多退' : '少退', " \xA5", formatDelta(delta.diff)))), m("div", {
      className: "Form-group ZS-RefundModal-col-tier"
    }, m("label", null, m("i", {
      className: "fas fa-shield-halved",
      style: "color:var(--zs-purple-8b5cf6, #8b5cf6)"
    }), " \u5BA1\u6279\u6863"), preview ? m("div", {
      className: 'ZS-RefundModal-slot filled ' + tierClassName(tierOrder(this.preview))
    }, m("span", {
      className: "slot-ic"
    }, m("i", {
      className: "fas fa-layer-group"
    })), m("div", {
      className: "slot-main"
    }, m("div", {
      className: "slot-txt"
    }, m("span", {
      className: 'ZS-RefundModal-tier ' + tierClassName(tierOrder(this.preview))
    }, preview.approval_label)), m("div", {
      className: "slot-txt",
      style: "margin-top:4px"
    }, preview.tier_hint))) : m("div", {
      className: "ZS-RefundModal-slot"
    }, m("span", {
      className: "slot-ic"
    }, m("i", {
      className: "fas fa-layer-group"
    })), m("div", {
      className: "slot-main"
    }, m("div", {
      className: "slot-txt"
    }, "\u63D0\u4EA4\u540E\u7531\u7CFB\u7EDF\u56DE\u586B\u3002")))), delta && delta.diff !== 0 && m("div", {
      className: "ZS-RefundModal-hint warn ZS-RefundModal-deltarow"
    }, m("i", {
      className: "fas fa-circle-exclamation"
    }), m("div", null, this.deltaHintText(delta)))), m("div", {
      className: "ZS-RefundModal-hint"
    }, m("i", {
      className: "fas fa-info-circle"
    }), m("div", null, "\u63D0\u4EA4\u540E\u8FDB\u5165\u5BA1\u6279\u6D41\u7A0B\uFF0C\u5361\u4ECD\u53EF\u6B63\u5E38\u7EA6\u8BFE\uFF0C\u76F4\u5230\u5BA1\u6279\u901A\u8FC7\u624D\u4F1A\u4F5C\u5E9F\u5E76\u6E05\u96F6\u5269\u4F59\u8BFE\u65F6\u3002")), this.error && m("div", {
      className: "ZS-RefundModal-error"
    }, this.error)), m("div", {
      className: "Modal-footer"
    }, m(Button, {
      className: "Button Button--secondary",
      onclick: () => this.hide()
    }, "\u53D6\u6D88"), m(Button, {
      className: "Button Button--primary ZS-RefundRequestModal-submit",
      disabled: this.saving || !this.canSubmit(),
      loading: this.saving,
      onclick: () => this.submit()
    }, "\u63D0\u4EA4\u7533\u8BF7")));
  }

  /**
   * 差额提示行的文案 (逐字照抄 brief §5.3)。
   *
   * · 高于: `高于系统核算金额 ¥{sys}，多退 ¥{d}；差额将在审批时一并提交财务复核，请在说明中写明原因。`
   * · 低于: `低于系统核算金额 ¥{sys}（少退 ¥{d}），将按填写金额 ¥{filled} 退款；差额将在审批时一并提交财务复核，请在说明中写明原因。`
   * · big (系统核算额落在**最低档**, 而手填额很大): 追加
   *   `审批档仍按系统核算额判定为一档「{approval_label}」；`
   *
   * ⚠️ 「一档」由**后端给的 `tier_order`** 翻成中文序数 (⛔ 前端不按金额判档);
   *   追加条件同样用 `tier_order === 1` (⛔ 前端不写任何金额阈值)。
   */
  deltaHintText(delta) {
    const sys = '¥' + formatAmount(delta.sys);
    const d = '¥' + formatDelta(delta.diff);
    if (delta.under) {
      return '低于系统核算金额 ' + sys + '（少退 ' + d + '），将按填写金额 ¥' + formatAmount(delta.filled) + ' 退款；差额将在审批时一并提交财务复核，请在说明中写明原因。';
    }
    let text = '高于系统核算金额 ' + sys + '，多退 ' + d + '；差额将在审批时一并提交财务复核，请在说明中写明原因。';

    // big 态特例: 系统核算额落在最低档 (用后端 tier_order 判, ⛔ 前端不写阈值)
    if (tierOrder(this.preview) === 1 && this.preview) {
      text += '审批档仍按系统核算额判定为' + tierOrdinalText(tierOrder(this.preview)) + '「' + this.preview.approval_label + '」；';
    }
    return text;
  }

  // ─────────────────────────────────────────────────────────────────────
  // 交互
  // ─────────────────────────────────────────────────────────────────────

  /** 切原因 ⇒ 重拉预览 (核算额会变) + 金额框重置 */
  pickReason(key) {
    if (this.reason === key || this.saving) return;
    this.reason = key;
    this.loadPreview(key);
  }

  /**
   * 能不能提交。
   *
   * ⛔ **不拦金额大小** —— 只拦三件「提交上去必然失败 / 没意义」的事:
   *   ① 预览还没回来 (没有系统核算额 ⇒ 算不出差额 ⇒ 说明必填判不准)
   *   ② 说明超过 500 字 (后端会 422)
   *   ③ 差额 ≠ 0 但说明为空 (brief §5.3 明确必填)
   */
  canSubmit() {
    if (this.saving) return false;
    if (!this.preview) return false;
    if (this.note.length > NOTE_MAX) return false;
    if (this.noteRequired() && this.note.trim() === '') return false;

    // 金额填了但不是数字 ⇒ 不能提交 (⛔ 绝不能把 NaN 发给后端:
    //   JSON.stringify(NaN) = 'null' ⇒ 后端当成「没填」⇒ 静默按系统核算额提交!)
    const filled = parseAmountInput(this.amount);
    if (filled !== null && !isFinite(filled)) return false;
    return true;
  }
  async submit() {
    if (!this.canSubmit()) return;

    // 金额非数字 ⇒ 本地拦下 (文案 = **后端同一句**, ⛔ 不自己编一套)
    const filled = parseAmountInput(this.amount);
    if (filled !== null && !isFinite(filled)) {
      this.error = '退款金额必须是数字';
      m.redraw();
      return;
    }
    this.saving = true;
    this.error = '';
    m.redraw();
    try {
      const result = await Api.requestRefund(this.card.id, {
        refund_reason: this.reason,
        // ⛔ 发**纯数字** (⛔ 不发带千分位的字符串); 没填 ⇒ 不发这个键 (后端按系统核算额)
        refund_amount: filled === null ? undefined : filled,
        refund_note: this.note
      });
      this.saving = false;

      // 🔴 v0.18.65 — ⛔ **不要**先 this.hide()
      //   Flarum 2.0 的 `app.modal.show()` **默认就把整个弹窗栈替换掉**
      //   (keepStack 默认 false ⇒ `modalList = [this.modal]`) ⇒ 本弹窗自动消失。
      //   ⛔ 而「先 hide() 再 show()」会**锁死全站弹窗**:
      //     hide() → ModalManager.animateHide() ⇒ `modalClosing = true`,
      //     并在**本弹窗的 DOM 元素**上挂 `transitionend` 监听;
      //     紧接着 show() 把本弹窗的 DOM 移除 ⇒ transitionend 永不触发
      //     ⇒ `state.close()` 永不执行 ⇒ `modalClosing` 永久停在 true
      //     ⇒ 之后**任何**弹窗的 hide() 都被闩锁挡住 ⇒ 取消 / × / Esc 全部失效。
      app.modal.show(RefundResultModal, {
        card: this.card,
        result
      });
    } catch (e) {
      this.error = errText(e, '提交失败');
      this.saving = false;
      m.redraw();
    }
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/modal/RefundRequestModal', RefundRequestModal);